import React, {
  Dispatch,
  FC,
  Fragment,
  HTMLAttributes,
  KeyboardEvent,
  ReactNode,
  SetStateAction,
} from 'react';
import { Transition } from '@headlessui/react';
import { CaretDown } from '..';
import { CardTitle } from '../text';
import { DateContainer } from './Date';

export interface SectionProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  titleVariant?: 'primary' | 'secondary';
  subtitle?: string;
  subSize?: 'md' | 'lg';
  children?: ReactNode;
  collaps?: boolean;
  open?: boolean;
  setOpen?: Dispatch<SetStateAction<boolean>>;
  range?: string;
}

export const Section: FC<SectionProps> = ({
  className,
  children,
  title,
  subSize,
  subtitle,
  collaps,
  open,
  setOpen,
  titleVariant,
  range,
  ...rest
}: SectionProps) => {
  const handleKeyPress = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      setOpen && setOpen(!open);
    }
  };

  const renderCollapsableSection = collaps ? (
    <>
      {!range && (
        <div
          className="w-full flex justify-start items-center gap-2 py-1 cursor-pointer"
          onClick={() => setOpen && setOpen((prev) => !prev)}
          onKeyPress={handleKeyPress}
          tabIndex={0}
          {...rest}
        >
          <CaretDown
            className={`fill-current text-thryv-black-500 -rotate-90 ${
              open && 'rotate-0'
            } transition-all duration-300 ease-in-out cursor-pointer`}
          />
          <span className="w-full flex flex-col justify-start items-start">
            <h6 className="capitalize text-card-title-h6 font-montserrat font-semibold text-thryv-black-500">
              {title}
            </h6>
            <span className="font-open-sans font-normal text-[13px] leading-[20px]">
              {subtitle}
            </span>
          </span>
        </div>
      )}
      {range && (
        <div
          className="w-full flex justify-between items-center py-1 cursor-pointer"
          onClick={() => setOpen && setOpen((prev) => !prev)}
          onKeyPress={handleKeyPress}
          tabIndex={0}
          {...rest}
        >
          <div className="w-2/4 flex justify-start items-center">
            <CaretDown
              className={`fill-current text-thryv-black-500 mr-2 -rotate-90 ${
                open && 'rotate-0'
              } transition-all duration-300 ease-in-out cursor-pointer`}
            />
            <span className="w-full flex flex-col justify-start items-start">
              <h6 className="capitalize text-card-title-h6 font-montserrat font-semibold text-thryv-black-500">
                {title}
              </h6>
              <span className="font-open-sans font-normal text-[13px] leading-[20px]">
                {subtitle}
              </span>
            </span>
          </div>
          <div className="w-2/4 flex justify-end items-center">
            <DateContainer range={range} />
          </div>
        </div>
      )}
      <Transition
        as={Fragment}
        show={open}
        enter="transition-all ease-in-out duration-100"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-all ease-in-out duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className={`${!open ? 'opacity-0 ' : 'opacity-100 '} mt-[18px]`}>
          {children}
        </div>
      </Transition>
    </>
  ) : null;

  const renderDefaultSection = !collaps ? (
    <>
      {titleVariant && titleVariant === 'primary' && (
        <CardTitle className="mb-6" variant="primary">
          {title}
        </CardTitle>
      )}

      {titleVariant && titleVariant === 'secondary' && (
        <CardTitle className="mb-6" variant="secondary">
          {title}
        </CardTitle>
      )}

      {subtitle && subSize === 'md' && (
        <CardTitle className="mb-3" variant="subtitle" size="md">
          {subtitle}
        </CardTitle>
      )}

      {subtitle && subSize && subtitle && subSize === 'lg' && (
        <CardTitle className="mb-3" variant="subtitle" size="lg">
          {subtitle}
        </CardTitle>
      )}

      {children}
    </>
  ) : null;

  return (
    <div
      data-testid="section"
      className={`w-full p-6 shadow-thryv rounded-sm bg-white ${
        className || ''
      }`}
    >
      {renderCollapsableSection}
      {renderDefaultSection}
    </div>
  );
};
